import React from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";

const SimilarTreks = ({ similarTreks }) => {
  if (!similarTreks?.length) return null;

  return (
    <div className="bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="section-heading text-foreground mb-3">
            <span className="text-primary">Similar</span> Adventures
          </h2>
          <div className="w-16 h-1 bg-primary rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Explore other incredible treks that match your adventure level and
            interests. Each offers unique experiences in the magnificent Himalayas.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {similarTreks?.map((trek, index) => {
            return (
              <div
                key={index}
                className="bg-[#fafaf7] rounded-2xl overflow-hidden border border-border/50 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={trek?.image}
                    alt={trek?.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

                  <div className="absolute top-4 left-4">
                    <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                      {[1, 2, 3, 4, 5].map((_, i) => (
                        <Icon
                          key={i}
                          name="Mountain"
                          size={12}
                          className={i < (trek?.difficulty || 0) ? "text-accent" : "text-gray-200"}
                        />
                      ))}
                    </div>
                  </div>

          
                  {trek?.isPopular && (
                    <div className="absolute bottom-4 left-4 bg-accent text-white px-3 py-1 rounded-full text-xs font-medium shadow-sm">
                      Popular Choice
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="MapPin" size={14} className="text-accent" />
                    <span className="text-sm font-medium text-accent">{trek?.region}</span>
                  </div>

                  <h3 className="font-headline text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors duration-200">
                    {trek?.name}
                  </h3>

                  <p className="text-text-secondary text-sm leading-relaxed mb-4 line-clamp-2">
                    {trek?.description}
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                      <Icon name="Calendar" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary">{trek?.duration} days</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                      <Icon name="TrendingUp" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary">{trek?.maxAltitude}</span>
                    </div>

                    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                      <Icon name="Thermometer" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary">{trek?.bestSeason}</span>
                    </div>
                  </div>

                  {trek?.highlights?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider mb-2">Highlights:</h4>
                      <div className="space-y-1">
                        {trek?.highlights?.slice(0, 3)?.map((highlight, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <Icon name="Check" size={12} className="text-success mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-text-secondary">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {trek?.rating && (
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={14}
                            className={i < (trek?.rating || 0) ? "text-accent fill-current" : "text-gray-200"}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-text-secondary">
                        {trek?.rating} {trek?.reviewCount ? `(${trek?.reviewCount} reviews)` : ''}
                      </span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 border-border text-xs" iconName="Eye" iconPosition="left">
                      View Details
                    </Button>
                    <Button variant="default" size="sm" className="flex-1 btn-conversion text-xs" iconName="Calendar" iconPosition="left">
                      Book Now
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Button
            variant="outline"
            size="lg"
            className="px-8"
            iconName="ArrowRight"
            iconPosition="right"
          >
            Explore All Treks
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SimilarTreks;
